import { useEffect, useState } from "react";
import { Calendar, Eye, Mail, MessageSquare, Search, Tag, X } from "lucide-react";

import { api } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Loader from "@/components/ui/loader";
import { Pagination } from "@/components/ui/pagination";
import {
  EmptyPanel,
  PageHero,
  PagePanel,
  PageShell,
  PanelHeading,
} from "@/components/interior-page";

const PAGE_SIZE = 10;

interface BuyerFeedback {
  id: string;
  user_id: string;
  user_name: string | null;
  user_email: string | null;
  reason: string | null;
  message: string;
  context: string;
  created_at: string;
}

interface FeedbackPage {
  items: BuyerFeedback[];
  total: number;
  page: number;
  page_size: number;
}

function fmtDateTime(s: string) {
  return new Date(s).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function AdminBuyerFeedbackPage() {
  const [data, setData] = useState<FeedbackPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [selected, setSelected] = useState<BuyerFeedback | null>(null);
  const toast = useToast();

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
    if (appliedSearch.trim()) params.set("search", appliedSearch.trim());
    api.get<FeedbackPage>(`/feedback/admin?${params}`)
      .then(setData)
      .catch(() => toast.error("Failed to load feedback."))
      .finally(() => setLoading(false));
  }, [page, appliedSearch, toast]);

  // reset to page 1 when the applied search changes
  useEffect(() => { setPage(1); }, [appliedSearch]);

  const resultCount = data?.total ?? 0;
  const displayed = data?.items ?? [];

  function applySearch() {
    setAppliedSearch(search);
    setPage(1);
  }

  return (
    <PageShell>
      <PageHero
        eyebrow="Admin"
        title="Buyer Feedback"
        description="Feedback buyers leave when they reach the cancellation dialog — review it to understand churn."
        aside={
          <div>
            <h2 className="text-lg font-bold text-foreground">Feedback snapshot</h2>
            <div className="mt-3 grid grid-cols-1 gap-2.5">
              <div className="public-glass-card rounded-xl p-3">
                <div className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Total received</div>
                <div className="mt-1.5 text-xl font-extrabold text-foreground">{resultCount}</div>
              </div>
            </div>
          </div>
        }
      />

      {/* Search */}
      <PagePanel>
        <div className="mb-4">
          <h2 className="text-base font-bold text-foreground">Search feedback</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">Find feedback by buyer name, email, reason, or message text.</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                className="pl-9 h-10 text-sm"
                placeholder="Search buyer, email, reason, message…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") applySearch(); }}
              />
            </div>
          </div>
          <Button type="button" onClick={applySearch} className="h-10 gap-2 rounded-full px-5">
            <Search className="h-3.5 w-3.5" />
            Search
          </Button>
          {(search || appliedSearch) && (
            <button
              type="button"
              onClick={() => { setSearch(""); setAppliedSearch(""); }}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </button>
          )}
        </div>
      </PagePanel>

      {/* Table */}
      <PagePanel>
        <PanelHeading
          title="All Feedback"
          description={data ? `${resultCount} entr${resultCount !== 1 ? "ies" : "y"} ${appliedSearch ? "found" : "total"}` : undefined}
        />

        {loading ? (
          <div className="flex justify-center py-12"><Loader /></div>
        ) : displayed.length === 0 ? (
          <EmptyPanel
            icon={MessageSquare}
            title="No feedback yet"
            description="Feedback buyers submit from the cancellation dialog will appear here."
          />
        ) : (
          <div>
            <div className="overflow-x-auto -mx-5 sm:-mx-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60 bg-muted/30">
                    <th className="py-3 pl-5 sm:pl-6 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Buyer</th>
                    <th className="py-3 px-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hidden sm:table-cell">Reason</th>
                    <th className="py-3 px-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Feedback</th>
                    <th className="py-3 px-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap hidden md:table-cell">Submitted</th>
                    <th className="py-3 pl-3 pr-5 sm:pr-6 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {displayed.map((fb) => (
                    <tr
                      key={fb.id}
                      onClick={() => setSelected(fb)}
                      className="group cursor-pointer transition-colors hover:bg-muted/25 align-top"
                    >
                      <td className="py-3.5 pl-5 sm:pl-6 pr-3 min-w-[160px]">
                        <div className="flex items-center gap-2.5">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold">
                            {(fb.user_name ?? "?").charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-foreground truncate">{fb.user_name ?? "Unknown buyer"}</p>
                            <p className="text-xs text-muted-foreground truncate">{fb.user_email ?? ""}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3.5 px-3 hidden sm:table-cell">
                        {fb.reason ? (
                          <span className="inline-flex items-center rounded-full border border-border/70 bg-muted/40 px-2.5 py-0.5 text-xs font-medium text-foreground whitespace-nowrap">
                            {fb.reason}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground/60">—</span>
                        )}
                      </td>
                      <td className="py-3.5 px-3 max-w-[420px]">
                        <p className="text-foreground/90 leading-relaxed line-clamp-2">{fb.message}</p>
                        <p className="mt-1 text-xs text-muted-foreground md:hidden">{fmtDateTime(fb.created_at)}</p>
                      </td>
                      <td className="py-3.5 px-3 text-xs text-muted-foreground whitespace-nowrap hidden md:table-cell">
                        {fmtDateTime(fb.created_at)}
                      </td>
                      <td className="py-3.5 pl-3 pr-5 sm:pr-6 text-right">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setSelected(fb); }}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-border/70 bg-background px-3 py-1.5 text-xs font-medium text-foreground shadow-sm transition-all hover:border-primary/50 hover:bg-primary/5 hover:text-primary"
                        >
                          <Eye className="h-3 w-3" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination page={page} totalElements={resultCount} pageSize={PAGE_SIZE} onChange={setPage} className="mt-1 px-5 sm:px-6" />
          </div>
        )}
      </PagePanel>

      {/* ── Feedback detail modal ── */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
          onClick={(e) => { if (e.target === e.currentTarget) setSelected(null); }}
        >
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90dvh] flex flex-col">
            {/* Header */}
            <div className="shrink-0 bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/15 text-white text-sm font-bold">
                    {(selected.user_name ?? "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-white leading-snug truncate">{selected.user_name ?? "Unknown buyer"}</p>
                    <p className="mt-0.5 flex items-center gap-1.5 text-xs text-white/60 truncate">
                      <Mail className="h-3 w-3 shrink-0" />
                      {selected.user_email ?? "—"}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="shrink-0 flex h-7 w-7 items-center justify-center rounded-lg bg-white/10 text-white/70 hover:bg-white/20 hover:text-white transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex flex-col gap-4 overflow-y-auto px-6 py-5">
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <Tag className="h-3.5 w-3.5" />
                  Reason:{" "}
                  {selected.reason ? (
                    <span className="inline-flex items-center rounded-full border border-border/70 bg-muted/40 px-2 py-0.5 font-medium text-foreground">
                      {selected.reason}
                    </span>
                  ) : (
                    <span className="text-muted-foreground/60">Not specified</span>
                  )}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  {fmtDateTime(selected.created_at)}
                </span>
              </div>

              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Feedback</p>
                <div className="rounded-xl border border-border/60 bg-muted/10 px-4 py-3.5 text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap">
                  {selected.message}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="shrink-0 border-t border-border px-6 py-4">
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="w-full h-11 rounded-xl bg-gold-gradient text-primary-foreground text-sm font-bold hover:brightness-[1.06] transition-[filter]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
